import { Checkbox, Grid, Paper } from "@mui/material";
import { useState, useEffect, useCallback, useContext } from "react";
import { CustomInput } from "../../../../shared/CustomInput";
import PlanActions from "../../../../shared/enrollment/planActions/PlanActions";
import PlanHeader from "../../../../shared/enrollment/planCode/PlanHeader";
import { AuthContext, GroupEnrollmentContext, ThemeContext, PaycheckContext } from "../../../../../contexts";
import CreateOutlinedIcon from "@mui/icons-material/CreateOutlined";
import { useHistory } from "react-router-dom";
import EventNoteIcon from "@mui/icons-material/EventNote";
import { helpingOurOwnLogo } from "../../../../../assets";

import "./PDForm.css";
import { dateTimeZoneConverter, getCurrentDate } from "../../../../../utils/commonFunctions/date";
import {
	EnrollmentSchema,
	HOOEnrollment as HOOEnrollmentPayload,
	OpenEnrollmentWithPlan
} from "../../../../../types/enrollment.types";
import { api } from "../../../../../utils/api";
import { HOOEnrollmentQueryType } from "../../../../../utils/api/enrollment/enrollment";
import { dollarize } from "../../../../../utils/commonFunctions/dollarize";
import { trackPromise } from "react-promise-tracker";

const PDForm = (): JSX.Element => {
	const { theme } = useContext(ThemeContext);
	const { member } = useContext(AuthContext);
	const { paycheck } = useContext(PaycheckContext);
	const [HOOEnrollment, setHOOEnrollment] = useState<EnrollmentSchema | null>(null);
	const [deductionStatus, setDeductionStatus] = useState<"CURRENT DEDUCTION" | "PENDING APPROVAL" | "WAIVED" | null>(
		null
	);
	const [premiumAmount, setPremiumAmount] = useState<string | null>("0.0");
	const [showWaiveEnrollmentButton, setShowWaiveEnrollmentButton] = useState<boolean | null>(null);
	const handleActivateEnrollmentClick = useCallback(async () => {
		if (!member && !premiumAmount) {
			return;
		}
		if (paycheck?.pay_frequency === "WEEKLY" && Number(premiumAmount) < 1.0) {
			alert("For weekly pay frequency premium amount should be greater than $1.0");
			return;
		}
		if (paycheck?.pay_frequency === "MONTHLY" && Number(premiumAmount) < 5.0) {
			alert("For monthly pay frequency premium amount should be greater than $5.0");
			return;
		}

		const HOOEnrollmentPayload: HOOEnrollmentPayload = {
			location_number: member.location_number,
			location_name: member.location.location_name,
			group_number: member.group_number,
			group_name: member.group.name,
			plan_code: "HOO-ALL",
			plan_name: "Helping Our Own",
			insured_object_id: member._id,
			insured_SSN: member.SSN,
			enrollment_status: null,
			waive_reason: "",
			enrollment_date: getCurrentDate(),
			premium_amount: premiumAmount ? Number(premiumAmount) : 0
		};
		const _HOOEnrollment = await trackPromise(api.enrollment.addNonOEEnrollment(HOOEnrollmentPayload));
		setHOOEnrollment(Object.assign({}, _HOOEnrollment));
		if (_HOOEnrollment) {
			alert("Thank you for enrolling . Your enrollment approval is pending");
		}
	}, [member, paycheck?.pay_frequency, premiumAmount]);

	const handleWaiveEnrollmentClick = useCallback(async () => {
		if (!member && !premiumAmount) {
			return;
		}

		const HOOEnrollmentPayload: HOOEnrollmentPayload = {
			location_number: member.location_number,
			location_name: member.location.location_name,
			group_number: member.group_number,
			group_name: member.group.name,
			plan_code: "HOO-ALL",
			plan_name: "Helping Our Own",
			insured_object_id: member._id,
			insured_SSN: member.SSN,
			enrollment_status: "WAIVED",
			waive_reason: "",
			enrollment_date: getCurrentDate(),
			premium_amount: Number(premiumAmount)
		};
		const _HOOEnrollment = await api.enrollment.addNonOEEnrollment(HOOEnrollmentPayload);
		setHOOEnrollment(Object.assign({}, _HOOEnrollment));
		if (_HOOEnrollment) {
			alert("Your enrollment has successfully waived");
		}
	}, [member, premiumAmount]);

	const getPremiumAmount = useCallback(() => {
		if (HOOEnrollment) {
			const { premium_amount, enrollment_status } = HOOEnrollment;
			if (enrollment_status === "ACTIVE") {
				setPremiumAmount(premium_amount + "");
				setDeductionStatus("CURRENT DEDUCTION");
			} else if (enrollment_status === "SUBMITTED") {
				setPremiumAmount(premium_amount + "");
				setDeductionStatus("PENDING APPROVAL");
			} else if (enrollment_status === "WAIVED") {
				setPremiumAmount("0.00");
				setDeductionStatus("WAIVED");
			} else {
				setPremiumAmount("0.00");
				setDeductionStatus(null);
			}
			if (enrollment_status === "WAIVED") {
				setShowWaiveEnrollmentButton(false);
			} else {
				setShowWaiveEnrollmentButton(true);
			}
		}
	}, [HOOEnrollment]);

	const handlePremiumAmountChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		const { value } = event.target;
		// console.log("value", value);
		if (!/^[0-9 .]*$/.test(value)) {
			// console.log("value insd", value);
			return;
		}
		// console.log("Number(value)", Number(value));
		setPremiumAmount(value);
	};

	// console.log("premiumAmount", premiumAmount);

	const getHOOEnrollment = useCallback(async () => {
		if (!member) {
			return;
		}
		const HOOEnrollmentQuery: HOOEnrollmentQueryType = {
			group_number: member.group_number,
			insured_SSN: member.SSN
		};
		const _HOOEnrollment = await api.enrollment.getHOOEnrollment(HOOEnrollmentQuery);
		if (_HOOEnrollment) {
			const { enrollment_status } = _HOOEnrollment;
			if (enrollment_status === "WAIVED") {
				setShowWaiveEnrollmentButton(false);
			} else {
				setShowWaiveEnrollmentButton(true);
			}
		} else {
			setShowWaiveEnrollmentButton(true);
		}
		setHOOEnrollment(Object.assign({}, _HOOEnrollment));
	}, [member]);

	useEffect(() => {
		getHOOEnrollment();
	}, [getHOOEnrollment]);

	useEffect(() => {
		getPremiumAmount();
	}, [HOOEnrollment, getPremiumAmount]);

	return (
		<div className="PDForm-container theme-container">
			<Paper className="paper-container">
				<div className="header-image-container">
					<PlanHeader planCode="HOO-ALL" planName="Payroll Deduction Form" planLogo={helpingOurOwnLogo} />
				</div>
				<div className="theme-plan-section-margin" />
				<div className="theme-plan-icon-item-container">
					<div>
						<CreateOutlinedIcon className="theme-plan-point-icon" />
					</div>
					<div className="theme-plan-point-text">
						The Brookshire Brothers Helping Our Own fund provides support to fellow Employee Owners who have
						suffered a significant financial hardship due to a house fire, natural disaster, family death,
						medical expenses or other unforeseen events.
					</div>
				</div>
				<br></br>
				<div className="theme-plan-icon-item-container">
					<div>
						<CreateOutlinedIcon className="theme-plan-point-icon" />
					</div>
					<div className="theme-plan-point-text">
						The fund enables you as an employee to provide support for your fellow Employee Owners during
						their time of need. You can opt to give either weekly or monthly through payroll deduction. All
						donations further the purposes of the Helping Our Own fund in its programs and operations to
						assist &quot;our own&quot; Brookshire Brothers employees
					</div>
				</div>
				<br></br>
				<div className="theme-plan-icon-item-container">
					<div>
						<CreateOutlinedIcon className="theme-plan-point-icon" />
					</div>
					<div className="theme-plan-point-text">
						All emplayers are eligible to apply, whether or not they donate to the Helping Our Own fund.
						Applications are strictly conlidential. Assistance granted is a gilt, not a loan. There is no
						expectation of re-payment.
					</div>
				</div>
				<Grid container className="theme-plan-section-margin">
					<Grid item xl={10} lg={10} md={10} sm={10} xs={10}>
						<div className="details-form-row">
							<div className="details-form-label  required">Employee ID</div>
							<CustomInput value={member?.member_id} disabled />
						</div>
					</Grid>
					<Grid item xl={10} lg={10} md={10} sm={10} xs={10}>
						<div className="details-form-row">
							<div className="details-form-label">First Name</div>
							<CustomInput value={member?.first_name} disabled />
						</div>
					</Grid>
					<Grid item xl={10} lg={10} md={10} sm={10} xs={10}>
						<div className="details-form-row">
							<div className="details-form-label">Last Name</div>
							<CustomInput value={member?.last_name} disabled />
						</div>
					</Grid>
					<Grid item xl={10} lg={10} md={10} sm={10} xs={10}>
						<div className="details-form-row">
							<div className="details-form-label">Date</div>
							<CustomInput value={dateTimeZoneConverter(getCurrentDate())} disabled />
						</div>
					</Grid>
					<Grid item xl={10} lg={10} md={10} sm={10} xs={10}>
						<div className="details-form-row">
							<div className="details-form-label">Department</div>
							<CustomInput value={member?.location?.location_name} />
						</div>
					</Grid>
				</Grid>
				<div className="details-form-row" style={{ marginTop: "2rem", width: "100%" }}>
					<div className="details-form-label  required">
						The following amount of payment per applicable payroll period (minimum $ 5 per month or $ 1.00
						per week) are to be deducted through Brookshire Brothers, Inc. Payroll for the purpose of
						depositing the funds into the Helping our own fund.)
					</div>
					<CustomInput value={premiumAmount} onChange={handlePremiumAmountChange} />
					<div className="theme-plan-inner-section-margin" />
					<div className="deduction-status theme-border-radius">{deductionStatus ? deductionStatus : ""}</div>
				</div>
				<div className="theme-plan-section-margin" />
				<div className="theme-plan-icon-item-container">
					<div>
						<EventNoteIcon className="event-icon" style={{ color: theme.primary_color }} />
					</div>
					<div className="theme-plan-text">
						<strong>Note:</strong> Employee represents that this authorization is executed voluntarily and
						has been made as a conditional of his/her continued employment.
					</div>
				</div>
				<div className="theme-plan-option-content theme-plan-section-margin">
					<Checkbox defaultChecked style={{ paddingLeft: 0 }} />
					<p className="theme-plan-checkbox-label">
						<strong>
							I UNDERSTAND AND AGREE TO THE TERMS AND CONDITIONS STATED ABOVE AND AUTHORIZE
							THE&nbsp;PAYROLL DEDUCTIONS TO BE MADE AS INDICATED
						</strong>
					</p>
				</div>
				<div className="theme-plan-section-margin" />
				<Grid container className="theme-plan-section-margin">
					<Grid item xl={6} lg={6} md={6} sm={12} xs={12}>
						<div className="details-form-row">
							<div className="details-form-label  required">Signature</div>
							<CustomInput value={member?.first_name + " " + member?.last_name} disabled />
						</div>
					</Grid>
					<Grid item xl={6} lg={6} md={6} sm={12} xs={12}>
						<div className="details-form-row">
							<div className="details-form-label  required">Date</div>
							<CustomInput value={dateTimeZoneConverter(getCurrentDate())} disabled />
						</div>
					</Grid>
				</Grid>
				<div className="theme-plan-section-margin" />
				<div className="theme-plan-icon-item-container">
					<div>
						<EventNoteIcon className="event-icon" style={{ color: theme.primary_color }} />
					</div>
					<div className="theme-plan-text">
						<strong>Note:</strong> PLEASE MAIL THE COMPLETED DEDUCTION FORM TO BROOKSHIRE BROTHERS HUMAN
						RESOURCE DEPARTMENT
					</div>
				</div>
				<div className="theme-plan-section-margin" />
				<PlanActions
					waiveButtonCallback={handleWaiveEnrollmentClick}
					activateButtonCallback={handleActivateEnrollmentClick}
				/>
			</Paper>
		</div>
	);
};

export default PDForm;
